import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

const baseFetchQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:4444',
  credentials: 'include',
  prepareHeaders: (headers) => {
    headers.set('Content-Type', 'application/json');
    headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
    headers.set('Pragma', 'no-cache');
    return headers;
  },
});

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await baseFetchQuery(args, api, extraOptions);

  if (result.meta?.response) {
    const authStep = result.meta.response.headers.get('X-Osirisai-Auth-Step');
    const accessTokenExpirationDate = result.meta.response.headers.get(
      'X-Osirisai-Access-Token-Exp',
    );
    const impersonation = result.meta.response.headers.get('X-Osirisai-Impersonation');

    if (authStep || accessTokenExpirationDate || impersonation) {
      api.dispatch({
        type: 'auth/updateAuthHeaders',
        payload: {
          authStep: authStep || null,
          accessTokenExpirationDate: accessTokenExpirationDate || null,
          impersonation: impersonation === 'true',
        },
      });
    }
  }

  return result;
};

const baseQueryWithRefreshAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Don't try to refresh token for auth endpoints
    const url = typeof args === 'string' ? args : args.url;
    const method = typeof args === 'string' ? 'GET' : args.method;

    if (url === '/auth' && (method === 'POST' || method === 'PATCH')) {
      return result;
    }

    const refreshResult = await baseQuery(
      {
        url: '/auth',
        method: 'PUT',
      },
      api,
      extraOptions,
    );

    if (refreshResult.data) {
      result = await baseQuery(args, api, extraOptions);
    }
    return result;
  }

  return result;
};

export const tagTypes = [
  'User',
  'UserAdmin',
  'Drone',
  'DroneOptions',
  'DroneSession',
  'App',
  'DroneApp',
  'Mission',
  'TriggerTemplate',
  'TrainingSet',
  'TrainingJob',
  'SwarmBehavior',
  'Simulation',
];

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithRefreshAuth,
  tagTypes: tagTypes,
  endpoints: () => ({}),
});
